import React from 'react';

import { ToastMessage } from '@cokitchen/cokitchen-components';
import { ConversionListFormType, ConversionListType, IngredientType } from 'models/pricing.model';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { UserProfileType } from 'models/user.model';
import { STORAGE_KEYS } from './constants';
import { BatchType } from 'models/item-request.model';
import moment from 'moment';

const MONTHS_OF_YEAR = [
  'Janaury',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const isDateValid = (date: string): boolean => {
  const dateValue = new Date(date);
  return !isNaN(dateValue.getTime());
};

const getOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
export const getDate = (date: string): number | null | string => {
  const dateValue = new Date(date);
  const value = dateValue.getDate();
  const ordinalValue = getOrdinal(value);
  return isDateValid(date) ? ordinalValue : null;
};

export const getMonth = (date: string): string | null => {
  const dateValue = new Date(date);
  return isDateValid(date) ? MONTHS_OF_YEAR[dateValue.getMonth() ?? 0] : null;
};

export const getShortMonth = (date: string): string | null => {
  const month = getMonth(date);
  return month ? month.slice(0, 3) : null;
};

export const getYear = (date: string): number | null => {
  const dateValue = new Date(date);
  return isDateValid(date) ? dateValue.getFullYear() : null;
};

export const getSimpleDateFormat = (date: string): string | null => {
  return `${getDate(date)} ${getShortMonth(date)} ${getYear(date)}`;
};

export const formatDate = (timeStr: string) => {
  const dateString = new Date(timeStr).toDateString();
  // remove day
  return dateString.split(' ').slice(1).join(' ');
};

export const formatTime = (timeStr: string) => {
  return Intl.DateTimeFormat('en-NG', {
    timeStyle: 'short',
    hourCycle: 'h12'
  }).format(new Date(timeStr));
};

export const formatPhoneNumber = (number: string) => {
  // add - after every three characters
  let formattedNumber = '';
  for (let index = 0; index < number.length - 1; index += 1) {
    formattedNumber += number[index];
    if ((index + 1) % 3 === 0) {
      formattedNumber += '-';
    }
  }
  return formattedNumber;
};

export const handleSelect = <T extends string | number>(selected: T, options: T[]): T[] => {
  const selectedOption = options.find((option) => option === selected);

  if (selectedOption != null) {
    return options.filter((option) => option !== selected);
  }

  return [...options, selected];
};

export const formatCurrency = (figure: number | string, options?: Intl.NumberFormatOptions) => {
  if (!figure && +figure !== 0) return '-';
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    ...options
  }).format(+figure);
};

export const formatNumberWithCommas = (x: string | number | undefined) => {
  if (x) {
    const splitNum = String(x)?.split('.');
    const wholeNum = splitNum[0];
    const floatNum = splitNum?.[1] || '';
    const numWithCommas = wholeNum?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return floatNum ? numWithCommas + '.' + floatNum : numWithCommas;
  } else return 0;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const debounce = <T extends (...args: any[]) => void>(func: T, wait: number) => {
  let timeout: NodeJS.Timeout | null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    const context = this as ThisParameterType<T>;

    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(() => {
      timeout = null;
      func.apply(context, args);
    }, wait);
  };
};

export const unwrapAmount = (amount: string) => {
  return Number(amount?.split('₦ ')[1]?.replace(/[^\d.]/g, ''));
};

export const calculateTotalPrice = (items?: IngredientType[]): number => {
  if (!items) return 0;
  return items?.flat()?.reduce((total, item) => {
    const quantity = parseFloat(item?.quantity ?? '0');
    const price = parseFloat(item?.ingredient?.min_vendor_price || '0');
    const newPrice = quantity * price;
    return newPrice ? total + newPrice : total;
  }, 0);
};

export const cleanPayload = <T extends Record<string, any>>(payload: T): T => {
  Object.keys(payload).forEach((key) => {
    if (
      !payload[key] ||
      (typeof payload[key] === 'object' && Object.keys(payload[key]).length === 0)
    ) {
      delete payload[key];
    }
  });
  return payload;
};

export const combineIngredientsAndPackaging = (data: any[]): any[] => {
  return data.reduce((acc, item) => {
    const combined = [...(item.ingredients || []), ...(item.packaging || [])];
    return acc.concat(combined);
  }, []);
};
export const findUpdatedQuantities = (newCombinedArray: any[], combinedArray: any[]): any[] => {
  return newCombinedArray.filter((newItem) => {
    const matchedItem = combinedArray.find((item) => item.item_id === newItem.item_id);
    return matchedItem && Number(matchedItem.quantity) !== Number(newItem.quantity);
  });
};
export const findNewIngredients = (ingredients: any[]): any[] => {
  return ingredients.filter((_) => !_.item_id);
};
export const findMissingIngredients = (combinedArray: any[], newCombinedArray: any[]): any[] => {
  return combinedArray.filter(
    (item) => !newCombinedArray.some((newItem) => newItem.item_id === item.item_id)
  );
};

export const toSnakeCase = (str: string) => str.toLowerCase().replace(/\s+/g, '_');

export const convertKeysToSnakeCase = <T extends Record<string, unknown>>(data: T[]): T[] =>
  data.map((row) => {
    const newRow: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(row)) {
      const snakeCaseKey = toSnakeCase(key);
      newRow[snakeCaseKey] = value;
    }

    return newRow as T;
  });

export const appendZeroToPhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.length === 10 ? `0${phoneNumber}` : phoneNumber;
};

export const initKitchenData = async () => {
  const url = 'internal/cokitchens';
  const res = await apiService.get(url, {
    baseURL: process.env.REACT_APP_BRAND_MGT_API_URL,
    isInternal: true
  });
  const data = res.data.data;
  return data;
};

export const handleApiError = (error: unknown, fallback = 'An error occurred') => {
  const message = (error as any)?.response?.data?.message;
  const messages = Array.isArray(message) ? message : [message];
  messages.forEach((message) => {
    toast(<ToastMessage type='error' message={message || fallback} />);
  });
};

export const updateLocalStorageUserData = (user?: UserProfileType) => {
  if (!user) return;
  const persistedFirstName = localStorage.getItem(STORAGE_KEYS.FIRST_NAME);
  const persistedLastName = localStorage.getItem(STORAGE_KEYS.LAST_NAME);
  const persistedEmail = localStorage.getItem(STORAGE_KEYS.EMAIL);

  if (user.first_name !== persistedFirstName) {
    localStorage.setItem(STORAGE_KEYS.FIRST_NAME, user.first_name);
  }
  if (user.last_name !== persistedLastName) {
    localStorage.setItem(STORAGE_KEYS.LAST_NAME, user.last_name);
  }
  if (user.email !== persistedEmail) {
    localStorage.setItem(STORAGE_KEYS.EMAIL, user.email);
  }
};

export const getUserFullName = (user?: UserProfileType) => {
  if (!user) return '';
  return `${user?.first_name} ${user?.last_name}`;
};

export const sortConvertionItem = (item: ConversionListType) => {
  const sortedArray: ConversionListFormType = {
    id: item?.id,
    name: item?.ingredient?.name,
    secton_name: item?.ingredient?.category,
    no_of_portions: item?.no_of_portions,
    portion_type: item?.portion_type,
    ingredients:
      item?.items
        ?.filter((_) => _.type === 'ingredient' || _.type === 'prep')
        ?.map((_) => ({
          ingredient_name: _.ingredient.name,
          quantity: String(_.quantity),
          type: _.type === 'prep' ? 'mix' : _.type,
          item_id: _.id,
          mix_id: item?.id,
          ingredient: _.ingredient
        })) || [],
    packaging:
      item?.items
        ?.filter((_) => _.type === 'packaging')
        ?.map((_) => ({
          ingredient_name: _.ingredient.name,
          quantity: String(_.quantity),
          type: _.type,
          item_id: _.id,
          mix_id: item?.id,
          ingredient: _.ingredient
        })) || []
  };
  return sortedArray;
};

export const reduceBatches = (batches: BatchType[]) => {
  const reduced = batches.reduce<{ batch_id?: string; batch_no: number; quantity: number }[]>(
    (acc, curr) => {
      const found = acc.find((item) => item.batch_no === curr.batch_no);
      if (found) {
        found.quantity += curr.quantity;
      } else {
        acc.push({ ...curr });
      }
      return acc;
    },
    []
  );
  return reduced;
};

export const getTotalQuantitySent = (batches: BatchType[]) => {
  return batches.reduce((quantity, batch) => quantity + Number(batch.quantity), 0);
};

export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string' || email.trim() === '') {
    return false;
  }

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return EMAIL_REGEX.test(email.trim());
};

export const isValidPhone = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string' || phone.trim() === '') {
    return false;
  }

  const cleaned = phone.trim().replace(/[\s\-()]/g, '');
  const PHONEREGEX = /^(\+?234|0)\d{10}$/;

  return PHONEREGEX.test(cleaned);
};

export const formatRequestDate = (date: string | Date): string => {
  return moment(date).format('Do MMM, YYYY [at] h:mm A');
};
